import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserIcon, 
  CogIcon,
  ArrowRightOnRectangleIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  
  // Refs to manage request cancellation and debouncing
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const isFetchingRef = useRef(false);

  // Debounced fetch function with request cancellation
  const fetchCartCount = useCallback(async () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce: only fetch if last fetch was more than 500ms ago
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    const DEBOUNCE_DELAY = 500;
    const MIN_FETCH_INTERVAL = 2000; // Minimum 2 seconds between actual requests

    if (isFetchingRef.current) {
      // If already fetching, schedule a retry after current request completes
      debounceTimerRef.current = setTimeout(() => {
        fetchCartCount();
      }, DEBOUNCE_DELAY);
      return;
    }

    if (timeSinceLastFetch < MIN_FETCH_INTERVAL) {
      // Too soon since last fetch, debounce it
      debounceTimerRef.current = setTimeout(() => {
        fetchCartCount();
      }, MIN_FETCH_INTERVAL - timeSinceLastFetch);
      return;
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;
    lastFetchTimeRef.current = Date.now();

    try {
      const response = await api.get('/api/cart/summary', {
        signal: abortControllerRef.current.signal
      });
      setCartCount(response.data.itemCount);
    } catch (error) {
      // Don't log errors for aborted/canceled requests
      const isCanceled = error.name === 'AbortError' || 
                        error.name === 'CanceledError' || 
                        error.code === 'ERR_CANCELED' ||
                        error.message?.toLowerCase().includes('cancel');
      if (!isCanceled) {
        console.error('Failed to fetch cart count:', error);
      }
    } finally {
      isFetchingRef.current = false;
      abortControllerRef.current = null;
    }
  }, []);

  // Consolidated effect for cart count fetching
  useEffect(() => {
    if (!isAuthenticated) {
      setCartCount(0);
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      return;
    }

    // Initial fetch on page load
    fetchCartCount();

    // Listen for custom cart update events (when cart is modified)
    const handleCartUpdate = () => {
      fetchCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      // Cancel any pending requests on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isAuthenticated, fetchCartCount]);

  // Debounced refresh on route change - only when pathname actually changes
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (!isAuthenticated) return;

    // Only fetch if pathname actually changed (not just location object reference)
    if (prevPathnameRef.current === location.pathname) {
      return;
    }
    
    prevPathnameRef.current = location.pathname;

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce route change fetches - increased to 1 second to reduce requests
    debounceTimerRef.current = setTimeout(() => {
      fetchCartCount();
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [location.pathname, isAuthenticated, fetchCartCount]);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setCartCount(0);
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/#hosting-plans' },
    { name: 'Contact', href: '/#contact' },
  ];

  const userMenu = [
    { name: 'Dashboard', href: '/dashboard', icon: CogIcon },
    { name: 'Profile', href: '/profile', icon: UserIcon },
    ...(user?.isAdmin ? [{ name: 'Admin', href: '/admin', icon: CogIcon }] : []),
  ];

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src="https://res.cloudinary.com/diat3n1ac/image/upload/v1772340205/The_Host_Bros_Logo_nj5kny.png"
                alt="The Host Bros"
                className="h-8 w-auto max-w-[140px] object-contain object-left"
              />
              <span className="ml-2 text-xl font-bold text-gray-900 hidden sm:inline">
                The Host Bros
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors"
              >
                {item.name}
              </Link>
            ))}
            {isAuthenticated && (
              <Link
                to="/dashboard"
                className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors"
              >
                My Plans
              </Link>
            )}
            {isAuthenticated && user?.isAdmin && (
              <Link
                to="/admin"
                className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors"
              >
                Admin
              </Link>
            )}
          </div>

          {/* Desktop Auth & Cart */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Cart Icon */}
                <Link
                  to="/cart"
                  className="relative text-gray-700 hover:text-primary-600 transition-colors"
                >
                  <ShoppingCartIcon className="h-6 w-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
                
                {/* User Menu */}
                <div className="relative group">
                  <button className="flex items-center text-gray-700 hover:text-primary-600 transition-colors">
                    <span className="mr-2">{user?.firstName}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-primary-600 p-2"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {isAuthenticated && (
              <Link
                to="/dashboard"
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                My Plans
              </Link>
            )}
            {isAuthenticated && user?.isAdmin && (
              <Link
                to="/admin"
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Admin
              </Link>
            )}
            
            {isAuthenticated ? (
              <div className="border-t border-gray-200 pt-4">
                {userMenu.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="flex items-center text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                ))}
                <Link
                  to="/cart"
                  className="flex items-center text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <ShoppingCartIcon className="w-5 h-5 mr-3" />
                  Cart ({cartCount})
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full text-gray-700 hover:text-primary-600 px-3 py-2 text-base font-medium"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                  Logout
                </button>
              </div>
            ) : (
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary block text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 